import { useCallback, useState } from 'react';

import { useStripe } from '@stripe/react-stripe-js';
import { AxiosError, AxiosResponse } from 'axios';
import { deriveConfirmationVariant } from 'components/SalesPortal/PurchaseConfirmation';
import dayjs from 'dayjs';
import { useTracking } from 'hooks/mixpanel/useTracking';
import useSalesPortalCheckout from 'hooks/salesPortal/useSalesPortalCheckout';
import useSalesPortalKiloClassFromUrlParam from 'pages/sales-portal/useKiloClassFromParam';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ErrorMessage,
  SalesPortalCheckoutRequest,
  SalesPortalCheckoutResponse,
  SalesPortalCheckoutSetupIntentRequiresActionResponse,
} from 'types';
import { preserveQueryParams } from 'utils/navigation';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import { CheckoutFormValues, useCheckout } from '../../CheckoutContext';
import { handleCheckoutError } from '../../utils';

function buildCheckoutPayload(
  formValues: CheckoutFormValues,
  kiloClassId: string | null,
  stripeSetupIntentId: string | null,
  athletePackageId: string,
  discountId: string | null,
): SalesPortalCheckoutRequest {
  return {
    athletePackageId,
    whoIsThisFor: formValues.whoIsThisFor,
    profileId: formValues.profileId,
    athleteInformation: {
      email: formValues.athleteInformation.email,
      firstName: formValues.athleteInformation.firstName,
      lastName: formValues.athleteInformation.lastName,
      phone: formValues.athleteInformation.phone ?? '',
      address: formValues.athleteInformation.address ?? null,
      medicalNotes: formValues.athleteInformation.medicalNotes ?? '',
      emergencyName: formValues.athleteInformation.emergencyName ?? '',
      emergencyPhone: formValues.athleteInformation.emergencyPhone ?? '',
      surname: formValues.athleteInformation.surname ?? '',
      dob: formValues.athleteInformation.dob
        ? dayjs(formValues.athleteInformation.dob).format('YYYY-MM-DD')
        : null,
      gender: formValues.athleteInformation.gender ?? null,
    },
    paymentMethod: {
      selectedPaymentMethodId: formValues.paymentMethod.selectedPaymentMethodId,
      stripeConfirmationToken: formValues.paymentMethod.stripeConfirmationToken,
    },
    // Always sent; the server re-derives what the step actually required (contract
    // markers, waiver on file) and ignores the block when nothing applies.
    agreement: {
      acknowledged: formValues.agreementWaiver.acknowledged,
      initials: formValues.agreementWaiver.initials.map((value, index) => ({ index, value })),
      signature: formValues.agreementWaiver.signature,
    },
    kiloClassId: kiloClassId ?? null,
    stripeSetupIntentId: stripeSetupIntentId ?? null,
    discount: discountId,
  };
}

interface UseConfirmPurchaseResult {
  confirmPurchase: () => void;
  isSubmitting: boolean;
  is3DSAuthenticating: boolean;
}

// The Confirm-purchase handler for the Order Summary sidebar. The checkout POST,
// the 202 → handleNextAction() → retry 3DS/SCA loop (DEV-2349), and the
// step-targeted error mapping all moved here verbatim from the deleted Review
// step — the request/response contract is untouched.
export default function useConfirmPurchase(): UseConfirmPurchaseResult {
  const stripe = useStripe();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { salesPortalSlug } = useParams<{ salesPortalSlug: string }>();
  const {
    form,
    packageData,
    billingPlan,
    appliedDiscount,
    setPhase,
    reopenStepForError,
    setCheckoutResult,
    setKiloClassErrorMessage,
  } = useCheckout();
  const queryClient = useQueryClient();

  const { errorNotification } = notificationHandler();
  const { trackSalesPortalPurchaseCompleted } = useTracking();
  const { mutate: checkoutMutate, isLoading } = useSalesPortalCheckout({
    gym: packageData.gym.gymId,
    packageId: packageData.packageId,
    billingPlanId: billingPlan.billingPlanId,
  });

  const { kiloClassId } = useSalesPortalKiloClassFromUrlParam();

  const [is3DSAuthenticating, setIs3DSAuthenticating] = useState<boolean>(false);

  const handleCheckoutErrorCallback = useCallback(
    (error: AxiosError<ErrorMessage>) => {
      handleCheckoutError(error, {
        onExitCheckout: (): void => {
          navigate(preserveQueryParams(`/sales-portal/${salesPortalSlug}`, searchParams));
        },
        onNavigateToStep: reopenStepForError,
        // Spec Item 4: a plan disabled mid-checkout fails forward into the
        // dedicated error screen rather than dumping the buyer back on the catalog.
        onBillingPlanError: (): void => {
          setPhase('billingPlanError');
        },
        // ENG-2367 (#3119): surface the backend's reason on the terminal screen
        // instead of discarding it — the message renders with the old copy as fallback.
        onKiloClassError: (message: string): void => {
          setKiloClassErrorMessage(message);
          setPhase('kiloClassError');
        },
        onShowError: (message: string): void => {
          errorNotification('The purchase could not be completed', message);

          if (message.includes('The email address is already in use by another athlete in the gym')) {
            form.setFieldError('athleteInformation.email', 'Email address is already in use');
          }

          if (
            message.includes(
              'The email address of the new linked account must be different from the primary account email',
            )
          ) {
            form.setFieldError(
              'athleteInformation.email',
              'The new linked account email must be different from your email address',
            );
          }

          if (message.includes('Cannot use the same phone number as the primary account')) {
            form.setFieldError(
              'athleteInformation.phone',
              'The phone number cannot be the same as the primary account phone number',
            );
          }
        },
      });
    },
    [
      navigate,
      salesPortalSlug,
      searchParams,
      reopenStepForError,
      setPhase,
      setKiloClassErrorMessage,
      errorNotification,
      form,
    ],
  );

  const handleCheckoutSuccess = useCallback(
    (response: AxiosResponse<SalesPortalCheckoutResponse>) => {
      setCheckoutResult(response.data);
      trackSalesPortalPurchaseCompleted(
        packageData.gym.gymId,
        packageData.packageId,
        packageData.type,
        billingPlan.billingPlanId,
        billingPlan.numberOfInstallments,
        'public',
        response.data.reservationOutcome,
        deriveConfirmationVariant(response.data),
      );
      // The buyer now has a purchase on file (returning-buyer detection, spec Item 9)
      // and a new package: mark the cached lookups stale instead of serving old data.
      queryClient.invalidateQueries('salesPortalAthleteByProfile');
      queryClient.invalidateQueries('athletePackages');
      setPhase('confirmation');
    },
    [
      setCheckoutResult,
      trackSalesPortalPurchaseCompleted,
      packageData.gym.gymId,
      packageData.packageId,
      packageData.type,
      billingPlan.billingPlanId,
      billingPlan.numberOfInstallments,
      queryClient,
      setPhase,
    ],
  );

  const handle3DSAuthentication = useCallback(
    async (
      setupIntentClientSecret: string,
      formValues: CheckoutFormValues,
      athletePackageId: string,
    ): Promise<void> => {
      if (!stripe) {
        errorNotification('Error', 'Stripe is not initialized. Please refresh the page and try again.');
        return;
      }

      setIs3DSAuthenticating(true);

      const { error, setupIntent } = await stripe.handleNextAction({
        clientSecret: setupIntentClientSecret,
      });

      if (error) {
        errorNotification(
          'Authentication Error',
          error.message ||
            'Failed to complete the authentication. Please try again or add a new payment method.',
        );
        reopenStepForError('payment');
        setIs3DSAuthenticating(false);
        return;
      }

      // Retry checkout with the confirmed setup intent
      const retryPayload = buildCheckoutPayload(
        formValues,
        kiloClassId ?? null,
        setupIntent?.id ?? null,
        athletePackageId,
        appliedDiscount?.discountId ?? null,
      );

      checkoutMutate(retryPayload, {
        onSuccess: handleCheckoutSuccess,
        onError: handleCheckoutErrorCallback,
        onSettled: () => setIs3DSAuthenticating(false),
      });
    },
    [
      stripe,
      kiloClassId,
      checkoutMutate,
      handleCheckoutSuccess,
      handleCheckoutErrorCallback,
      errorNotification,
      reopenStepForError,
      appliedDiscount,
    ],
  );

  const handleSuccessCheckoutResponse = useCallback(
    (
      response: AxiosResponse<
        SalesPortalCheckoutResponse | SalesPortalCheckoutSetupIntentRequiresActionResponse
      >,
      formValues: CheckoutFormValues,
      athletePackageId: string,
    ): void => {
      // Check if response is 202 (requires 3DS)
      if (response.status === 202) {
        const requiresActionData = response.data as SalesPortalCheckoutSetupIntentRequiresActionResponse;
        const { setupIntentClientSecret } = requiresActionData;

        if (!setupIntentClientSecret) {
          errorNotification('Error', 'Unable to complete payment authentication. Missing client secret.');
          return;
        }

        handle3DSAuthentication(setupIntentClientSecret, formValues, athletePackageId);
        return;
      }

      // Success response (200)
      const successData = response.data as SalesPortalCheckoutResponse;
      handleCheckoutSuccess({ ...response, data: successData });
    },
    [errorNotification, handle3DSAuthentication, handleCheckoutSuccess],
  );

  const confirmPurchase = useCallback((): void => {
    // Generate athletePackageId once and reuse it for retry
    const athletePackageId = uuidv4();
    const formValues = form.getTransformedValues();
    const payload = buildCheckoutPayload(
      formValues,
      kiloClassId ?? null,
      null,
      athletePackageId,
      appliedDiscount?.discountId ?? null,
    );

    checkoutMutate(payload, {
      onSuccess: response => handleSuccessCheckoutResponse(response, formValues, athletePackageId),
      onError: handleCheckoutErrorCallback,
    });
  }, [
    form,
    kiloClassId,
    checkoutMutate,
    handleSuccessCheckoutResponse,
    handleCheckoutErrorCallback,
    appliedDiscount,
  ]);

  return {
    confirmPurchase,
    isSubmitting: isLoading,
    is3DSAuthenticating,
  };
}
